import { useRef } from "react";
import { X, Check } from "lucide-react";
import { Reveal, Tag, H2 } from "@/components/sections/shared";
import { ParticleCard, GlobalSpotlight, useMobileDetection } from "@/components/MagicBento/MagicBento";

const oldWay = [
  "Wait for the insurance company to explain itself",
  "Try to decode confusing letters alone",
  "Accept low offers because you feel stuck",
  "Search Google for generic answers",
  "Hire help before knowing if you need it",
];

const newWay = [
  "Ask Jack what may be happening",
  "Run your claim through the Rip-Off Detector",
  "Understand possible red flags",
  "Get recommended next steps",
  "Make informed decisions with more confidence",
];

export default function Shift() {
  const gridRef = useRef(null);
  const isMobile = useMobileDetection();

  return (
    <section id="shift" data-testid="shift-section" className="relative bg-[#121214] py-24 md:py-32">
      <GlobalSpotlight
        gridRef={gridRef}
        disableAnimations={isMobile}
        glowColor="138, 43, 226"
        spotlightRadius={280}
      />
      <div className="max-w-[1280px] mx-auto px-6 lg:px-14">
        <Reveal>
          <Tag testId="shift-tag">The Shift</Tag>
          <H2>Stop guessing. Start understanding.</H2>
          <p className="mt-6 max-w-3xl text-[#A1A1AA] font-light leading-relaxed">
            Most policyholders are forced to make major decisions with very little information.
            Should you accept the offer? Should you appeal? Should you ask for more documentation?
            Should you bring in a lawyer or public adjuster? Should you push back or move on?
            Inssux gives you a clearer starting point. Not hype. Not legal promises. Not empty
            encouragement. Just plain-English claim guidance designed to help you understand where
            you stand.
          </p>
        </Reveal>

        <div ref={gridRef} className="bento-section mt-14 grid lg:grid-cols-2 gap-6">
          <Reveal>
            <ParticleCard
              testId="old-way-card"
              disableAnimations={isMobile}
              glowColor="230, 57, 70"
              className="magic-bento-card magic-bento-card--border-glow h-full bg-[#0A0A0C] border border-white/10 p-8 md:p-10"
            >
              <p className="font-mono-file text-[11px] tracking-[0.3em] text-[#71717A]">THE OLD WAY</p>
              <ul className="mt-7 space-y-5">
                {oldWay.map((item) => (
                  <li key={item} className="flex items-start gap-4">
                    <X size={16} className="mt-1 shrink-0 text-[#E63946]/70" />
                    <span className="text-[#71717A] font-light leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </ParticleCard>
          </Reveal>
          <Reveal delay={0.15}>
            <ParticleCard
              testId="inssux-way-card"
              disableAnimations={isMobile}
              glowColor="138, 43, 226"
              className="magic-bento-card magic-bento-card--border-glow h-full bg-[#0A0A0C] border border-[#8A2BE2]/50 p-8 md:p-10 shadow-[0_0_60px_-20px_rgba(138,43,226,0.5)]"
              overflowContent={
                <div className="absolute -top-3 left-8 z-10 bg-[#8A2BE2] px-3 py-1 font-mono-file text-[9px] tracking-[0.25em] text-white">
                  THE UPGRADE
                </div>
              }
            >
              <p className="font-mono-file text-[11px] tracking-[0.3em] text-[#9D4EDD]">THE INSSUX WAY</p>
              <ul className="mt-7 space-y-5">
                {newWay.map((item) => (
                  <li key={item} className="flex items-start gap-4">
                    <Check size={16} className="mt-1 shrink-0 text-[#FFD166]" />
                    <span className="text-white/90 font-light leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </ParticleCard>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
